/**
 * @fileoverview Server transport — ships buffered browser log entries to the API's client-log ingest
 * (BROWSER-TELEMETRY-001 P3) over the ONE sanctioned transport, `@puffin-core/api-client`.
 *
 * Why the client and not `fetch`: production `fetch` is banned (`web/no-bespoke-http-transport`)
 * because a bare call owns neither auth, cancellation, envelope decoding nor tracing; the api-client
 * owns all four, so a shipped batch is itself traced and joins the page's span tree, and it carries
 * the page's bearer token when there is one (the ingest reads identity from it via `optionalAuth`).
 * The one exception is the HIDE flush, which goes through `navigator.sendBeacon` — the platform's
 * keep-alive-after-unload primitive. It runs when the document becomes HIDDEN (`visibilitychange`,
 * the last event a mobile browser reliably delivers before it may discard the page) and again on
 * `pagehide` (the desktop unload path); the second is a no-op when the first emptied the buffer.
 * Both listeners sit on the WINDOW: `visibilitychange` is fired at the document and bubbles, so a
 * window listener runs AFTER every document listener — including `web-vitals`' own, which reports
 * the final LCP / CLS / INP from exactly that event. The flush therefore never beats the values it
 * exists to carry, whatever order the listeners were registered in. A beacon cannot set
 * `Authorization`, so a hide batch arrives anonymous; the ingest is OPEN by design, and every entry
 * still carries the page's `correlationId`/`traceId`, which join it to the page's authenticated
 * requests in Loki. No `fetch(keepalive)` fallback exists: it would buy the header at the price of a
 * second, bespoke transport, and the join keys already recover the identity.
 *
 * Delivery discipline (Q-UMB-13 — every rule below replaced a defect in the BROWSER-TELEMETRY-001
 * sender, and `server.delivery.test.js` was falsified against that sender on 2026-09-25: 15 of its
 * 16 cases go red on it):
 *   - every entry is conformed to the ingest's closed schema and every batch is cut to its bounds
 *     (`./wireContract.js`), so one bad entry can no longer poison a batch;
 *   - ONE flush in flight at a time;
 *   - a failure is CLASSIFIED: a transient one (network, 408/425/429, 5xx) restores the batch and
 *     backs off exponentially with jitter up to a cap; a refusal (other 4xx) drops only that batch;
 *     an absent ingest (404/405/410, or HTML where JSON belongs — the SPA answering) disables the
 *     transport for the page, because every later batch would fail identically;
 *   - identical entries are storm-suppressed and counted (`./repeatSuppressor.js`).
 *
 * Two ways in: `log(entry)` is the LOGGER's path and applies the shipping policy (`minLevel`,
 * `shipInfo`); `enqueue(entry)` is for a producer that has already made its own shipping decision
 * (the web-vitals collector samples per page and gates on consent) and must not be re-judged by a
 * policy written for log lines. Both then share every delivery rule above.
 *
 * Wire contract: `POST <endpoint>` `{ source: 'client', logs: [{ timestamp, level, message, data?,
 * context?, traceId?, spanId?, correlationId?, url? }] }` — `@puffin-core/client-log-routes`
 * `clientLogBatchSchema`.
 *
 * @module transports/server
 */

import { LOG_LEVEL, LOG_LEVEL_PRIORITY } from '../constants.js';
import { WIRE_LIMITS, sizeWireEntry, takeableCount, toWireEntry } from './wireContract.js';
import { createRepeatSuppressor } from './repeatSuppressor.js';

/** The fleet-wide mount path (`@puffin-core/client-log-routes` `INGEST_PATH`). */
export const CLIENT_LOG_INGEST_PATH = '/v1/telemetry/logs';

/**
 * INFO shipping policies (CLIENT-LOG-SAMPLING-001). WARNING+ always ships; INFO is the volume
 * question, and the answer is ONE sampling decision per trace, never one per entry:
 *
 * - `sampled-traces` (default): an INFO entry ships iff the page trace it belongs to is sampled
 *   (`entry.sampled`, stamped by `Logger.log` from `@puffin-core/tracing-browser`). A sampled
 *   trace then arrives COMPLETE — the API's spans and request logs plus the browser's INFO lines —
 *   and an unsampled trace carries only WARNING+.
 * - `always`: every INFO entry ships (a debugging window, not a production default).
 * - `never`: WARNING+ only.
 */
export const SHIP_INFO = Object.freeze({
  SAMPLED_TRACES: 'sampled-traces',
  ALWAYS: 'always',
  NEVER: 'never',
});
const SHIP_INFO_VALUES = new Set(Object.values(SHIP_INFO));

/** Statuses that mean "try again later" — the request never reached a verdict. */
const TRANSIENT_STATUSES = new Set([0, 408, 425, 429]);
/** Statuses that mean "there is no ingest here" — nothing this page sends will ever land. */
const ABSENT_INGEST_STATUSES = new Set([404, 405, 410]);

/** What a failed flush does with the batch it took. */
export const FAILURE_ACTION = Object.freeze({
  RETRY: 'retry',
  DROP: 'drop',
  DISABLE: 'disable',
});

const DEFAULT_CONFIG = Object.freeze({
  endpoint: CLIENT_LOG_INGEST_PATH,
  batchSize: WIRE_LIMITS.maxLogsPerBatch,
  batchInterval: 5000,
  maxBuffer: 500,
  minLevel: LOG_LEVEL.WARN,
  shipInfo: SHIP_INFO.SAMPLED_TRACES,
  retryBaseMs: 5000,
  retryMaxMs: 300000,
  repeatWindowMs: 60000,
  maxRepeatsPerWindow: 5,
});

/**
 * Classify a rejected post. The api-client normalises an HTTP failure to `{ status }` (0 for a
 * network error); an abort is a `DOMException`; an HTML body on a JSON route is a typed
 * `HtmlResponseError` — the SPA answered, so the ingest is not behind this URL at all.
 *
 * @param {*} err
 * @returns {string} one of {@link FAILURE_ACTION}
 */
export function classifyFailure(err) {
  if (err?.name === 'HtmlResponseError') return FAILURE_ACTION.DISABLE;
  const status = typeof err?.status === 'number' ? err.status : 0;
  if (ABSENT_INGEST_STATUSES.has(status)) return FAILURE_ACTION.DISABLE;
  if (TRANSIENT_STATUSES.has(status) || status >= 500) return FAILURE_ACTION.RETRY;
  return FAILURE_ACTION.DROP;
}

function resolveBeacon(explicit) {
  if (explicit !== undefined) return explicit;
  const nav = globalThis.navigator;
  return nav && typeof nav.sendBeacon === 'function' ? nav.sendBeacon.bind(nav) : null;
}

function withSuppressedCount(wire, suppressedRepeats) {
  if (suppressedRepeats === 0) return wire;
  return { ...wire, data: { ...wire.data, suppressedRepeats } };
}

/**
 * Batched shipper for browser log entries.
 */
export class ServerTransport {
  /**
   * @param {Object} config
   * @param {Object} config.client - the app's `@puffin-core/api-client` instance (`post`, `defaults.baseURL`)
   * @param {string} [config.endpoint='/v1/telemetry/logs'] - ingest path, relative to the client's base URL
   * @param {number} [config.batchSize=50] - flush when this many entries are buffered (capped at the ingest's 50)
   * @param {number} [config.batchInterval=5000] - flush at least this often (ms) while entries wait
   * @param {number} [config.maxBuffer=500] - hard cap; the OLDEST entry is dropped beyond it
   * @param {string} [config.minLevel='WARNING'] - ship this level and above (`LOG_LEVEL` values)
   * @param {string} [config.shipInfo='sampled-traces'] - INFO policy, one of `SHIP_INFO`
   * @param {number} [config.retryBaseMs=5000] - first backoff after a transient failure
   * @param {number} [config.retryMaxMs=300000] - backoff ceiling
   * @param {number} [config.repeatWindowMs=60000] - storm-suppression window per identical entry
   * @param {number} [config.maxRepeatsPerWindow=5] - identical entries admitted per window
   * @param {Function|null} [config.sendBeacon] - unload flusher; defaults to `navigator.sendBeacon`
   * @param {string} [config.baseURL] - absolute API base for the unload beacon; defaults to the
   *   client's `defaults.baseURL` (a fetch-adapter client without one passes it explicitly)
   * @param {EventTarget|null} [config.target=globalThis] - the window: where `pagehide` and the
   *   bubbled `visibilitychange` are observed; `null` for a non-browser host
   * @param {() => number} [config.random=Math.random] - backoff jitter source
   * @param {() => number} [config.now=Date.now] - clock for storm suppression
   */
  constructor(config = {}) {
    const {
      client,
      sendBeacon,
      baseURL,
      target = globalThis,
      random = Math.random,
      now = Date.now,
      ...rest
    } = config;
    if (!client || typeof client.post !== 'function') {
      throw new TypeError(
        "ServerTransport requires `client` (the app's @puffin-core/api-client instance)",
      );
    }
    this.client = client;
    this.config = { ...DEFAULT_CONFIG, ...rest };
    if (!SHIP_INFO_VALUES.has(this.config.shipInfo)) {
      throw new TypeError(
        `ServerTransport: shipInfo must be one of ${[...SHIP_INFO_VALUES].join(' | ')}; got ${JSON.stringify(this.config.shipInfo)}`,
      );
    }
    this.beacon = resolveBeacon(sendBeacon);
    this.random = random;
    this.suppressor = createRepeatSuppressor({
      windowMs: this.config.repeatWindowMs,
      maxPerWindow: this.config.maxRepeatsPerWindow,
      now,
    });
    /** Sized wire entries, oldest first: `{ wire, bytes }`. */
    this.buffer = [];
    this.flushTimer = null;
    this.inFlight = null;
    this.consecutiveFailures = 0;
    this.disabled = false;
    this.stats = { dropped: 0, suppressed: 0, refused: 0 };
    this.baseURL = baseURL;
    this.target = target;
    /** Producers that must hand over what they hold before the hide flush (`beforeHide`). */
    this.hideHooks = new Set();
    this.onPageHide = () => this.flushOnHide();
    this.onVisibilityChange = () => {
      if (this.target.document?.visibilityState === 'hidden') this.flushOnHide();
    };
    if (this.target) {
      this.target.addEventListener('pagehide', this.onPageHide);
      this.target.addEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  /**
   * Whether an entry ships: its level clears `minLevel`, or it is INFO and the `shipInfo` policy
   * keeps it — under `sampled-traces`, iff the entry's page trace is sampled.
   * @param {Object} entry - a logger entry (`level`, `sampled`)
   * @returns {boolean}
   */
  shouldShip(entry) {
    const priority = LOG_LEVEL_PRIORITY[entry.level];
    if (priority <= LOG_LEVEL_PRIORITY[this.config.minLevel]) return true;
    if (entry.level !== LOG_LEVEL.INFO) return false;
    switch (this.config.shipInfo) {
      case SHIP_INFO.ALWAYS:
        return true;
      case SHIP_INFO.SAMPLED_TRACES:
        return entry.sampled === true;
      default:
        return false;
    }
  }

  /**
   * Buffer one logger entry (called by `Logger.log`) if the shipping policy keeps it. Never throws.
   * @param {Object} entry
   */
  log(entry) {
    if (this.shouldShip(entry)) this.enqueue(entry);
  }

  /**
   * Buffer one entry whose producer has ALREADY decided it ships — the policy in
   * {@link ServerTransport#shouldShip} is not applied; every delivery rule is. Never throws.
   * @param {Object} entry - a logger-shaped entry (`timestamp`, `level`, `message`, …)
   */
  enqueue(entry) {
    if (this.disabled) return;
    const wire = toWireEntry(entry);
    const verdict = this.suppressor.admit(`${wire.level}\u0000${wire.message}`);
    if (!verdict.admit) {
      this.stats.suppressed += 1;
      return;
    }
    this.buffer.push(sizeWireEntry(withSuppressedCount(wire, verdict.suppressedRepeats)));
    if (this.buffer.length > this.config.maxBuffer) {
      this.buffer.shift();
      this.stats.dropped += 1;
    }
    if (this.isBackingOff() || this.inFlight) return;
    if (this.buffer.length >= this.batchCeiling()) {
      this.flush();
      return;
    }
    if (!this.flushTimer) this.scheduleFlush(this.config.batchInterval);
  }

  batchCeiling() {
    return Math.min(this.config.batchSize, WIRE_LIMITS.maxLogsPerBatch);
  }

  isBackingOff() {
    return this.consecutiveFailures > 0;
  }

  scheduleFlush(delayMs) {
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flush();
    }, delayMs);
  }

  hasScheduledFlush() {
    return this.flushTimer !== null;
  }

  getBufferSize() {
    return this.buffer.length;
  }

  /**
   * Delivery counters since construction — `dropped` (buffer cap), `suppressed` (storm), `refused`
   * (entries in batches the ingest refused) — plus the live backoff/circuit state.
   * @returns {{dropped: number, suppressed: number, refused: number, consecutiveFailures: number, disabled: boolean}}
   */
  getStats() {
    return {
      ...this.stats,
      consecutiveFailures: this.consecutiveFailures,
      disabled: this.disabled,
    };
  }

  /** Remove up to one batch (≤ the ingest's entry and byte bounds) from the head of the buffer. */
  takeBatch() {
    return this.buffer.splice(0, takeableCount(this.buffer, this.batchCeiling()));
  }

  restore(batch) {
    const merged = [...batch, ...this.buffer];
    const overflow = Math.max(0, merged.length - this.config.maxBuffer);
    this.stats.dropped += overflow;
    this.buffer = merged.slice(overflow);
  }

  clearTimer() {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }

  /**
   * Ship everything buffered, one bounded batch at a time, through the sanctioned client. Only one
   * drain runs at a time: a call while one is in flight waits for it and then drains what is left,
   * so an entry buffered after the running drain's last check is still covered. Never throws.
   * @returns {Promise<void>}
   */
  flush() {
    if (this.inFlight) return this.inFlight.then(() => this.flush());
    this.clearTimer();
    this.inFlight = this.drain().finally(() => {
      this.inFlight = null;
      // An entry logged between the drain's last check and this settle found a flush in flight
      // and scheduled nothing; it must not wait for the NEXT log call to be shipped.
      if (this.buffer.length > 0 && !this.flushTimer && !this.disabled) {
        this.scheduleFlush(this.config.batchInterval);
      }
    });
    return this.inFlight;
  }

  async drain() {
    while (this.buffer.length > 0 && !this.disabled) {
      const batch = this.takeBatch();
      try {
        await this.client.post(
          this.config.endpoint,
          { source: 'client', logs: batch.map((sized) => sized.wire) },
          { authRequired: false },
        );
        this.consecutiveFailures = 0;
      } catch (err) {
        // Telemetry never surfaces a failure to the page; it decides what the batch is worth.
        this.onFailure(classifyFailure(err), batch);
        return;
      }
    }
  }

  onFailure(action, batch) {
    if (action === FAILURE_ACTION.DISABLE) {
      this.stats.dropped += batch.length;
      this.disable();
      return;
    }
    if (action === FAILURE_ACTION.DROP) {
      // Whatever is still buffered is picked up by the flush the drain's settle schedules.
      this.stats.refused += batch.length;
      return;
    }
    this.restore(batch);
    this.consecutiveFailures += 1;
    this.scheduleFlush(this.backoffDelay());
  }

  /**
   * Exponential backoff with "equal jitter": half the step is fixed, half random, so a fleet of
   * tabs that failed together does not retry together.
   * @returns {number} ms until the next attempt
   */
  backoffDelay() {
    const step = Math.min(
      this.config.retryMaxMs,
      this.config.retryBaseMs * 2 ** (this.consecutiveFailures - 1),
    );
    return Math.round(step / 2 + (step / 2) * this.random());
  }

  /** The hard stop: nothing is buffered, scheduled or sent again on this page. */
  disable() {
    this.disabled = true;
    this.clearTimer();
    this.stats.dropped += this.buffer.length;
    this.buffer = [];
  }

  absoluteEndpoint() {
    const base = this.baseURL ?? this.client.defaults?.baseURL ?? '';
    return `${base.replace(/\/$/, '')}${this.config.endpoint}`;
  }

  /**
   * Register a producer that holds entries of its own and must hand them over BEFORE the hide
   * flush (the web-vitals collector releases its held metrics here). A hook that throws is
   * skipped — telemetry must never become the page's failure on its way out.
   * @param {() => void} hook
   * @returns {() => void} unregister
   */
  beforeHide(hook) {
    this.hideHooks.add(hook);
    return () => this.hideHooks.delete(hook);
  }

  /** The hide path: every `beforeHide` hook, then one beacon. */
  flushOnHide() {
    for (const hook of this.hideHooks) {
      try {
        hook();
      } catch (_hookErr) {
        // A producer failing on the way out must not cost the entries already buffered.
      }
    }
    this.flushWithBeacon();
  }

  /**
   * Hide path: the platform's keep-alive primitive. ONE bounded batch — the beacon queue shares
   * a 64 KiB quota, exactly the ingest's body limit. A refused beacon keeps the batch (a bfcache
   * restore, or the tab becoming visible again, flushes it normally).
   */
  flushWithBeacon() {
    if (!this.beacon || this.disabled || this.buffer.length === 0) return;
    const batch = this.takeBatch();
    const blob = new Blob(
      [JSON.stringify({ source: 'client', logs: batch.map((sized) => sized.wire) })],
      { type: 'application/json' },
    );
    if (!this.beacon(this.absoluteEndpoint(), blob)) this.restore(batch);
  }

  destroy() {
    this.clearTimer();
    if (this.target) {
      this.target.removeEventListener('pagehide', this.onPageHide);
      this.target.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
    this.hideHooks.clear();
    this.buffer = [];
  }
}

/**
 * Factory form.
 * @param {Object} config - see {@link ServerTransport}
 * @returns {ServerTransport}
 */
export function createServerTransport(config) {
  return new ServerTransport(config);
}
